import React, { useState } from 'react';
import { Calendar, Star, Shirt, Award, Clock, Footprints, Watch, Bot } from 'lucide-react';
import AIAssistant from './AIAssistant';
import '../styles/components/Profile.css';

const Profile = ({
   user = {},
   userProfile = {},
   posts = [],
   interests = [],
   myCloset = {},
   profileTab = 'main',
   setProfileTab = () => { },
   closetCategory = 'shirts',
   setClosetCategory = () => { },
   selectedDate = null,
   setSelectedDate = () => { },
   showOutfitPlanner = false,
   setShowOutfitPlanner = () => { },
   plannedOutfit = {},
   setPlannedOutfit = () => { },
   getDaysInMonth = () => [],
   getClosetItems = () => [],
   chatMessages = [],
   sendMessage = () => { },
   daysUntilReward = 7,
   weeklyOutfits = {},
   calendarPrivacy = {}
}) => {
   // Local state for planner
   const [localPlannedOutfit, setLocalPlannedOutfit] = useState({ occasion: '', items: [] });

   // Default user data
   const currentUser = {
      name: user.name || userProfile.name || 'Alex Chen',
      username: user.username || userProfile.username || '@alexstyle',
      streak: user.streakDays || userProfile.streak || 47,
      points: user.points || userProfile.points || 2840,
      level: user.level || 'Gold'
   };

   // Default badges data
   const defaultBadges = [
      {
         name: '7-Day Streak Master',
         earned: '2025-01-20',
         points: 100,
         description: 'Posted daily outfits for 7 consecutive days',
         icon: '🔥',
         rarity: 'Common'
      },
      {
         name: 'Style Influencer',
         earned: '2025-01-18',
         points: 250,
         description: 'Received 100+ likes across all posts',
         icon: '✨',
         rarity: 'Rare'
      },
      {
         name: 'Brand Explorer',
         earned: '2025-01-15',
         points: 150,
         description: 'Added items from 10 different brands to interests',
         icon: '🏷️',
         rarity: 'Uncommon'
      },
      {
         name: 'Trendsetter',
         earned: '2025-01-12',
         points: 300,
         description: 'First to post a trending outfit style',
         icon: '👑',
         rarity: 'Epic'
      },
      {
         name: 'Shopaholic',
         earned: null,
         points: 500,
         description: 'Make 10 purchases through the app',
         icon: '🛍️',
         rarity: 'Legendary',
         progress: '7/10'
      },
      {
         name: 'Charity Champion',
         earned: null,
         points: 500,
         description: 'Donated 10+ clothing items to charity',
         icon: '💝',
         rarity: 'Epic',
         progress: '7/10'
      }
   ];

   const badges = userProfile?.badges || defaultBadges;

   // Default closet data
   const defaultClosetCategories = {
      shirts: [
         { item: 'White Button Down', brand: 'Uniqlo', worn: '3 days ago' },
         { item: 'Black Turtleneck', brand: 'COS', worn: '1 week ago' },
         { item: 'Navy Blazer', brand: 'Zara', worn: '5 days ago' },
         { item: 'Oversized Hoodie', brand: 'Nike', worn: '2 days ago' }
      ],
      shoes: [
         { item: 'White Sneakers', brand: 'Adidas', worn: 'Yesterday' },
         { item: 'Black Boots', brand: 'Dr. Martens', worn: '2 days ago' },
         { item: 'Heels', brand: 'Jimmy Choo', worn: '1 week ago' },
         { item: 'Running Shoes', brand: 'Nike', worn: '3 days ago' }
      ],
      accessories: [
         { item: 'Silver Watch', brand: 'Casio', worn: 'Today' },
         { item: 'Leather Belt', brand: 'Coach', worn: '3 days ago' },
         { item: 'Statement Earrings', brand: 'Tiffany', worn: '1 week ago' },
         { item: 'Clutch Bag', brand: 'Gucci', worn: '2 weeks ago' }
      ]
   };

   const closetCategories = Object.keys(myCloset).length > 0 ? myCloset : defaultClosetCategories;

   // Calendar logic
   const CalendarScreen = () => {
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
      const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
      const firstDayWeekday = firstDayOfMonth.getDay();
      const daysInMonth = lastDayOfMonth.getDate();

      const monthNames = ["January", "February", "March", "April", "May", "June",
         "July", "August", "September", "October", "November", "December"];
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

      const calendarDays = [];

      for (let i = 0; i < firstDayWeekday; i++) {
         calendarDays.push(null);
      }

      for (let day = 1; day <= daysInMonth; day++) {
         calendarDays.push(day);
      }

      const formatDate = (day) => {
         if (!day) return null;
         return `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      };

      const getOutfitForDate = (day) => {
         if (!day) return null;
         const dateStr = formatDate(day);
         return posts.find(post => post.date === dateStr);
      };

      const isToday = (day) => {
         return day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
      };

      const isFutureDate = (day) => {
         if (!day) return false;
         const dateStr = formatDate(day);
         const date = new Date(dateStr);
         return date > today;
      };

      const handleDayClick = (day) => {
         if (!day) return;
         const dateStr = formatDate(day);
         setSelectedDate({ day, dateStr });

         if (isFutureDate(day) || isToday(day)) {
            setShowOutfitPlanner(true);
            const existingOutfit = getOutfitForDate(day);
            if (existingOutfit) {
               setLocalPlannedOutfit({
                  occasion: existingOutfit.occasion || '',
                  items: existingOutfit.outfit || []
               });
            } else {
               setLocalPlannedOutfit({ occasion: '', items: [] });
            }
         }
      };

      const addItemToOutfit = (item) => {
         if (!localPlannedOutfit.items.includes(item.item)) {
            setLocalPlannedOutfit(prev => ({
               ...prev,
               items: [...prev.items, item.item]
            }));
         }
      };

      const removeItemFromOutfit = (itemToRemove) => {
         setLocalPlannedOutfit(prev => ({
            ...prev,
            items: prev.items.filter(item => item !== itemToRemove)
         }));
      };

      if (showOutfitPlanner) {
         return (
            <div className="profile-screen">
               <div className="profile-header">
                  <button onClick={() => setShowOutfitPlanner(false)} className="back-button">←</button>
                  <h2 className="screen-title">Plan Outfit for {monthNames[currentMonth]} {selectedDate?.day}</h2>
               </div>

               <div className="planner-form">
                  <div className="form-group">
                     <label className="form-label">Occasion</label>
                     <input
                        type="text"
                        value={localPlannedOutfit.occasion}
                        onChange={(e) => setLocalPlannedOutfit(prev => ({ ...prev, occasion: e.target.value }))}
                        placeholder="What's the occasion?"
                        className="text-input"
                     />
                  </div>

                  <div className="form-group">
                     <h3 className="section-title">Selected Items ({localPlannedOutfit.items.length})</h3>
                     {localPlannedOutfit.items.length > 0 ? (
                        <div className="selected-items">
                           {localPlannedOutfit.items.map((item, idx) => (
                              <div key={idx} className="selected-item">
                                 <span className="item-name">{item}</span>
                                 <button
                                    onClick={() => removeItemFromOutfit(item)}
                                    className="remove-button"
                                 >
                                    Remove
                                 </button>
                              </div>
                           ))}
                        </div>
                     ) : (
                        <p className="empty-state">No items selected yet</p>
                     )}
                  </div>

                  <div className="form-group">
                     <h3 className="section-title">Choose from Your Closet</h3>
                     <div className="closet-categories">
                        {Object.entries(closetCategories).map(([category, items]) => (
                           <div key={category} className="category-section">
                              <h4 className="category-title">{category}</h4>
                              <div className="category-items">
                                 {items.map((item, idx) => (
                                    <button
                                       key={idx}
                                       onClick={() => addItemToOutfit(item)}
                                       className={`closet-item ${localPlannedOutfit.items.includes(item.item) ? 'selected' : ''
                                          }`}
                                    >
                                       <div className="item-info">
                                          <span className="item-name">{item.item}</span>
                                          <span className="item-brand">{item.brand}</span>
                                       </div>
                                    </button>
                                 ))}
                              </div>
                           </div>
                        ))}
                     </div>
                  </div>

                  <button
                     className="save-outfit-button"
                     onClick={() => {
                        console.log('Saving outfit for', selectedDate.dateStr, localPlannedOutfit);
                        setShowOutfitPlanner(false);
                     }}
                  >
                     Save Outfit Plan
                  </button>
               </div>
            </div>
         );
      }

      return (
         <div className="profile-screen">
            <div className="profile-header">
               <button onClick={() => setProfileTab('main')} className="back-button">←</button>
               <h2 className="screen-title">Outfit Calendar</h2>
            </div>

            <div className="calendar-container">
               <div className="calendar-header">
                  <h3 className="calendar-month">{monthNames[currentMonth]} {currentYear}</h3>
               </div>

               <div className="calendar-days-header">
                  {dayNames.map(day => (
                     <div key={day} className="day-header">{day}</div>
                  ))}
               </div>

               <div className="calendar-grid">
                  {calendarDays.map((day, index) => {
                     const outfit = getOutfitForDate(day);
                     const isCurrentDay = isToday(day);
                     const canPlan = day && (isFutureDate(day) || isCurrentDay);

                     return (
                        <button
                           key={index}
                           onClick={() => handleDayClick(day)}
                           disabled={!day}
                           className={`calendar-day ${!day ? 'invisible' :
                                 isCurrentDay ? 'today' :
                                    outfit ? 'has-outfit' :
                                       canPlan ? 'can-plan' :
                                          'past'
                              }`}
                        >
                           {day && (
                              <>
                                 <span className="day-number">{day}</span>
                                 {outfit && <div className="outfit-indicator" />}
                              </>
                           )}
                        </button>
                     );
                  })}
               </div>

               <div className="calendar-legend">
                  <div className="legend-item">
                     <div className="legend-dot today"></div>
                     <span>Today</span>
                  </div>
                  <div className="legend-item">
                     <div className="legend-dot has-outfit"></div>
                     <span>Has outfit saved</span>
                  </div>
                  <div className="legend-item">
                     <div className="legend-dot can-plan"></div>
                     <span>Click to plan future outfits</span>
                  </div>
               </div>

               <div className="recent-outfits">
                  <h3 className="section-title">Recent Outfits</h3>
                  <div className="outfits-list">
                     {posts
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .slice(0, 5)
                        .map((post, idx) => (
                           <div key={idx} className="outfit-item">
                              <div className="outfit-info">
                                 <div className="outfit-dot"></div>
                                 <div className="outfit-details">
                                    <p className="outfit-date">{new Date(post.date).toLocaleDateString()}</p>
                                    <p className="outfit-occasion">{post.occasion || 'Daily outfit'}</p>
                                 </div>
                              </div>
                              <button className="view-button">View</button>
                           </div>
                        ))}
                  </div>
               </div>
            </div>
         </div>
      );
   };

   // Interests Screen
   const InterestsScreen = () => (
      <div className="profile-screen">
         <div className="profile-header">
            <button onClick={() => setProfileTab('main')} className="back-button">←</button>
            <h2 className="screen-title">Interests</h2>
         </div>

         <div className="reward-card">
            <div className="reward-content">
               <div className="reward-icon">🎁</div>
               <div className="reward-info">
                  <div className="reward-days">{daysUntilReward} Days Left</div>
                  <div className="reward-text">Until your free reward!</div>
               </div>
            </div>
            <div className="reward-progress">
               <div
                  className="progress-bar"
                  style={{ width: `${((14 - daysUntilReward) / 14) * 100}%` }}
               ></div>
            </div>
            <div className="reward-description">
               Keep posting daily to unlock a special item from your interests!
            </div>
         </div>

         <div className="interests-grid">
            {interests.map((item, idx) => (
               <div key={idx} className="interest-card">
                  <div className="interest-image">
                     <Shirt size={32} className="placeholder-icon" />
                  </div>
                  <div className="interest-info">
                     <p className="interest-brand">{item.brand}</p>
                     <p className="interest-item">{item.item}</p>
                     <p className="interest-price">{item.price}</p>
                     <span className="reward-badge">💎 Reward Eligible</span>
                  </div>
               </div>
            ))}
         </div>
      </div>
   );

   // Closet Screen
   const ClosetScreen = () => (
      <div className="profile-screen">
         <div className="profile-header">
            <button onClick={() => setProfileTab('main')} className="back-button">←</button>
            <h2 className="screen-title">My Closet</h2>
         </div>

         <div className="closet-filters">
            {Object.keys(closetCategories).map(category => (
               <button
                  key={category}
                  onClick={() => setClosetCategory(category)}
                  className={`filter-button ${closetCategory === category ? 'active' : ''}`}
               >
                  {category}
               </button>
            ))}
         </div>

         <div className="closet-items">
            {closetCategories[closetCategory]?.map((item, idx) => (
               <div key={idx} className="closet-item-card">
                  <div className="closet-item-icon">
                     {closetCategory === 'shirts' && <Shirt size={20} />}
                     {closetCategory === 'shoes' && <Footprints size={20} />}
                     {closetCategory === 'accessories' && <Watch size={20} />}
                  </div>
                  <div className="closet-item-info">
                     <p className="closet-item-name">{item.item}</p>
                     <p className="closet-item-brand">{item.brand}</p>
                  </div>
                  <span className="closet-item-worn">{item.worn}</span>
               </div>
            ))}
         </div>
      </div>
   );

   // Badges Screen
   const BadgesScreen = () => (
      <div className="profile-screen">
         <div className="profile-header">
            <button onClick={() => setProfileTab('main')} className="back-button">←</button>
            <h2 className="screen-title">Badge Rewards</h2>
         </div>

         <div className="badges-stats">
            <div className="stats-card">
               <p className="stats-label">Total Badges Earned</p>
               <p className="stats-number">{badges.filter(b => b.earned).length}/{badges.length}</p>
               <p className="stats-points">
                  Points Earned: {badges.filter(b => b.earned).reduce((sum, b) => sum + b.points, 0)}
               </p>
            </div>
         </div>

         <div className="badges-section">
            <h3 className="section-title">Earned Badges</h3>
            <div className="badges-list">
               {badges.filter(badge => badge.earned).map((badge, idx) => (
                  <div key={idx} className="badge-card earned">
                     <div className="badge-icon">{badge.icon}</div>
                     <div className="badge-info">
                        <div className="badge-header">
                           <h4 className="badge-name">{badge.name}</h4>
                           <span className={`rarity-badge ${badge.rarity.toLowerCase()}`}>
                              {badge.rarity}
                           </span>
                        </div>
                        <p className="badge-description">{badge.description}</p>
                        <p className="badge-earned">Earned: {badge.earned}</p>
                     </div>
                     <div className="badge-points">
                        <span className="points-value">+{badge.points}</span>
                        <span className="points-label">points</span>
                     </div>
                  </div>
               ))}
            </div>
         </div>

         <div className="badges-section">
            <h3 className="section-title">In Progress</h3>
            <div className="badges-list">
               {badges.filter(badge => !badge.earned).map((badge, idx) => (
                  <div key={idx} className="badge-card in-progress">
                     <div className="badge-icon">{badge.icon}</div>
                     <div className="badge-info">
                        <div className="badge-header">
                           <h4 className="badge-name">{badge.name}</h4>
                           <span className={`rarity-badge ${badge.rarity.toLowerCase()}`}>
                              {badge.rarity}
                           </span>
                        </div>
                        <p className="badge-description">{badge.description}</p>
                        <div className="badge-progress">
                           {badge.progress && (
                              <span className="progress-badge">{badge.progress}</span>
                           )}
                        </div>
                     </div>
                     <div className="badge-points">
                        <span className="points-value">+{badge.points}</span>
                        <span className="points-label">points</span>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </div>
   );

   // AI Screen
   const AIScreen = () => (
      <div className="profile-screen">
         <div className="profile-header">
            <button onClick={() => setProfileTab('main')} className="back-button">←</button>
            <h2 className="screen-title">AI Fashion Stylist</h2>
         </div>

         <AIAssistant
            chatMessages={chatMessages}
            sendMessage={sendMessage}
         />
      </div>
   );

   // Render screens
   if (profileTab === 'calendar') return <CalendarScreen />;
   if (profileTab === 'interests') return <InterestsScreen />;
   if (profileTab === 'closet') return <ClosetScreen />;
   if (profileTab === 'badges') return <BadgesScreen />;
   if (profileTab === 'ai') return <AIScreen />;

   // Main Profile Screen
   return (
      <div className="profile-container">
         <div className="profile-main">
            <div className="user-profile">
               <div className="user-avatar"></div>
               <h2 className="user-name">{currentUser.name}</h2>
               <p className="user-username">{currentUser.username}</p>
               <div className="user-stats">
                  <span className="stat-item"><strong>{currentUser.streak}</strong> day streak</span>
                  <span className="stat-item"><strong>{currentUser.points}</strong> points</span>
               </div>
            </div>

            <div className="profile-menu">
               <button
                  onClick={() => setProfileTab('interests')}
                  className="menu-button"
               >
                  <Star size={24} />
                  <span className="menu-label">Interests</span>
               </button>
               <button
                  onClick={() => setProfileTab('closet')}
                  className="menu-button"
               >
                  <Shirt size={24} />
                  <span className="menu-label">My Closet</span>
               </button>
               <button
                  onClick={() => setProfileTab('calendar')}
                  className="menu-button"
               >
                  <Calendar size={24} />
                  <span className="menu-label">Outfit Calendar</span>
               </button>
               <button
                  onClick={() => setProfileTab('badges')}
                  className="menu-button"
               >
                  <Award size={24} />
                  <span className="menu-label">Badges</span>
               </button>
               <button
                  onClick={() => setProfileTab('ai')}
                  className="menu-button ai-button"
               >
                  <Bot size={24} />
                  <span className="menu-label">AI Fashion Stylist</span>
               </button>
            </div>
         </div>
      </div>
   );
};

export default Profile;